import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { CreateContext } from '../product/CreateContext';
import { useProducts } from '../product/ProductContext';
import { storage } from '../product/productStorage';
import { CREATE_KINDS, avatarColors, buildPromptContext, initials } from '../product/productUtils';
import { platformIdFor } from '../utils/publishing';
import HookScreen from './HookScreen';
import DescriptionScreen from './DescriptionScreen';
import ImageScreen from './ImageScreen';
import VideoScreen from './VideoScreen';
import BlogScreen from './create/BlogScreen';
import AdCopyScreen from './create/AdCopyScreen';
import ScriptScreen from './create/ScriptScreen';
import ReviewStep from './create/ReviewStep';
import { EMPTY_FLOW, FlowContext, FlowStepContext } from './create/flow';
import './create/flow.css';

// The older Hook, Caption, Image and Video tools are steps of each Create screen.
// Each step hands its output to the next; Review & post puts the post together.
// hint: what to do while the step has no output; done: what the step bar says once it has
const REVIEW = { id: 'review', label: 'Review & post', Screen: ReviewStep };
const STEPS = {
    social: [
        { id: 'hook', label: 'Hook', Screen: HookScreen, hint: 'Pick a hook to continue.', done: 'Hook picked' },
        { id: 'caption', label: 'Caption', Screen: DescriptionScreen, hint: 'Write captions and choose the one to post.', done: 'Caption ready' },
        { id: 'image', label: 'Image', Screen: ImageScreen, optional: true, hint: 'Generate an image, or skip this step.', done: 'Image ready' },
        REVIEW,
    ],
    blog: [
        { id: 'article', label: 'Article', Screen: BlogScreen, hint: 'Write the article to continue.', done: 'Article ready' },
        { id: 'image', label: 'Cover image', Screen: ImageScreen, optional: true, hint: 'Generate a cover image, or skip this step.', done: 'Cover image ready' },
        REVIEW,
    ],
    ads: [
        { id: 'copy', label: 'Ad copy', Screen: AdCopyScreen, hint: 'Write ad copy and choose a variant.', done: 'Ad copy ready' },
        { id: 'image', label: 'Creative', Screen: ImageScreen, optional: true, hint: 'Generate a creative, or skip this step.', done: 'Creative ready' },
        REVIEW,
    ],
    video: [
        { id: 'hook', label: 'Hook', Screen: HookScreen, hint: 'Pick a hook to continue.', done: 'Hook picked' },
        { id: 'script', label: 'Script', Screen: ScriptScreen, hint: 'Write the script to continue.', done: 'Script ready' },
        { id: 'video', label: 'Video', Screen: VideoScreen, optional: true, hint: 'Generate the video, or skip this step.', done: 'Video ready' },
        { id: 'caption', label: 'Caption', Screen: DescriptionScreen, hint: 'Write captions and choose the one to post.', done: 'Caption ready' },
        REVIEW,
    ],
};

// Kept per product (and per kind) so a reload picks up where you left off
const FLOW_KEY = 'createFlow';
const MEDIA_STEPS = ['image', 'video'];

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const str = (value) => (typeof value === 'string' ? value : '');
const strings = (value) => (Array.isArray(value) ? value.filter(item => typeof item === 'string') : []);
const flags = (value) => (isObject(value) ? Object.keys(value).reduce((acc, key) => (value[key] === true ? { ...acc, [key]: true } : acc), {}) : {});
const without = (object, keys) => Object.keys(object).reduce((acc, key) => (keys.includes(key) ? acc : { ...acc, [key]: object[key] }), {});

// Opened for a different planned post (or none): the saved flow belongs to something else
const seedKeyOf = (seed) => (seed ? [seed.planWeek, seed.postId, seed.topic, seed.hook].map(str).join('|') : '');

const readScript = (value) => (isObject(value) && Array.isArray(value.beats) ? {
    title: str(value.title),
    hook: str(value.hook),
    beats: value.beats.filter(isObject).map(beat => ({ time: str(beat.time), visual: str(beat.visual), voiceover: str(beat.voiceover), onScreenText: str(beat.onScreenText) })),
    cta: str(value.cta),
} : null);

const readArticle = (value) => (isObject(value) && str(value.body) ? {
    title: str(value.title),
    metaDescription: str(value.metaDescription),
    outline: strings(value.outline),
    body: str(value.body),
} : null);

const readAd = (value) => (isObject(value) ? {
    angle: str(value.angle),
    headline: str(value.headline),
    primaryText: str(value.primaryText),
    description: str(value.description),
    cta: str(value.cta),
    placement: str(value.placement),
    placementLabel: str(value.placementLabel),
    fields: strings(value.fields),
    limits: isObject(value.limits) ? value.limits : {},
} : null);

const freshFlow = (steps, seed) => {
    const has = (id) => steps.some(step => step.id === id);
    const hook = has('hook') ? str(seed?.hook).trim() : '';
    const caption = has('caption') ? str(seed?.caption).trim() : '';
    return {
        ...EMPTY_FLOW,
        topic: str(seed?.topic).trim(),
        hook,
        caption,
        hashtags: caption ? strings(seed?.hashtags) : [],
        captionPlatform: platformIdFor(seed?.platform),
        // A planned post's own hook and drafted caption count as done; picking or writing new ones replaces them
        done: { ...(hook && { hook: true }), ...(caption && { caption: true }) },
    };
};

const loadFlow = (kind, steps, seed) => {
    const saved = storage.getJson(FLOW_KEY, {})?.[kind];
    if (!isObject(saved) || saved.seedKey !== seedKeyOf(seed)) return { flow: freshFlow(steps, seed), resumed: false };
    const flow = {
        ...EMPTY_FLOW,
        topic: str(saved.topic),
        hook: str(saved.hook),
        hooks: strings(saved.hooks),
        script: readScript(saved.script),
        caption: str(saved.caption),
        hashtags: strings(saved.hashtags),
        captionPlatform: str(saved.captionPlatform),
        link: str(saved.link),
        article: readArticle(saved.article),
        ad: readAd(saved.ad),
        imagePrompt: str(saved.imagePrompt),
        // Generated media isn't kept, so those steps start again
        done: without(flags(saved.done), [...MEDIA_STEPS, 'review']),
        skipped: flags(saved.skipped),
    };
    return { flow, resumed: Object.keys(flow.done).length > 0 };
};

const same = (a, b) => a === b || (typeof a === 'object' && typeof b === 'object' && JSON.stringify(a) === JSON.stringify(b));

const prefersReducedMotion = () => {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {
        return false;
    }
};

const CreateScreen = ({ kind }) => {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { currentProduct: product, icps } = useProducts();
    const steps = STEPS[kind];
    const seed = location.state?.seed || null;
    const seedKey = seedKeyOf(seed);
    const [initial] = useState(() => loadFlow(kind, steps, seed));
    const [flow, setFlow] = useState(initial.flow);
    const [stepId, setStepId] = useState(steps[0].id);
    // Starting over remounts the steps, so their own fields start fresh too
    const [version, setVersion] = useState(0);
    const tabsRef = useRef(null);
    const moved = useRef(false);

    // ?icp= preselects the ICP ("Create for Nadia"); otherwise the primary one
    const icpId = searchParams.get('icp') ?? (icps.find(icp => icp.isPrimary) || icps[0])?.id ?? '';
    const icp = icps.find(item => item.id === icpId) || null;
    const icpIndex = icps.findIndex(item => item.id === icpId);

    const selectIcp = (id) => {
        const next = new URLSearchParams(searchParams);
        next.set('icp', id);
        setSearchParams(next, { replace: true, state: location.state });
    };

    const context = useMemo(() => ({
        product,
        icp,
        promptContext: buildPromptContext(product, icp),
        contentType: CREATE_KINDS[kind].contentType,
        seed,
        embedded: true,
    }), [product, icp, kind, seed]);

    /* ---------- Flow state ---------- */

    const update = useCallback((patch) => {
        setFlow(previous => (Object.keys(patch).every(key => same(previous[key], patch[key])) ? previous : { ...previous, ...patch }));
    }, []);

    const report = useCallback((id, patch, isDone) => {
        setFlow(previous => {
            const unchanged = Object.keys(patch).every(key => same(previous[key], patch[key]))
                && Boolean(previous.done[id]) === Boolean(isDone)
                && (!isDone || !previous.skipped[id]);
            if (unchanged) return previous;
            return {
                ...previous,
                ...patch,
                done: isDone ? { ...previous.done, [id]: true } : without(previous.done, [id]),
                skipped: isDone ? without(previous.skipped, [id]) : previous.skipped,
            };
        });
    }, []);

    useEffect(() => {
        const all = storage.getJson(FLOW_KEY, {});
        const { image, video, ...rest } = flow;
        storage.setJson(FLOW_KEY, { ...(isObject(all) ? all : {}), [kind]: { ...rest, seedKey, savedAt: new Date().toISOString() } });
    }, [flow, kind, seedKey]);

    const goTo = useCallback((id, { focus = false } = {}) => {
        moved.current = true;
        setStepId(id);
        if (focus) {
            window.requestAnimationFrame(() => tabsRef.current?.querySelector(`[data-step="${id}"]`)?.focus({ preventScroll: true }));
        }
    }, []);

    // A new step starts at its top, with its tab in view in the (scrolling) step list
    useEffect(() => {
        if (!moved.current) return;
        const list = tabsRef.current;
        if (!list) return;
        const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
        if (list.getBoundingClientRect().top < 0) list.scrollIntoView({ block: 'start', behavior });
        const tab = list.querySelector('.is-active');
        if (tab && list.scrollWidth > list.clientWidth) {
            const listBox = list.getBoundingClientRect();
            const tabBox = tab.getBoundingClientRect();
            list.scrollTo({ left: list.scrollLeft + (tabBox.left - listBox.left) - (listBox.width - tabBox.width) / 2, behavior });
        }
    }, [stepId]);

    const startOver = useCallback(() => {
        if (!window.confirm('Start over? What you made in these steps is cleared.')) return;
        setFlow(freshFlow(steps, seed));
        setVersion(previous => previous + 1);
        goTo(steps[0].id, { focus: true });
    }, [steps, seed, goTo]);

    const flowValue = useMemo(() => ({ flow, update, report, kind, steps, goTo, startOver }), [flow, update, report, kind, steps, goTo, startOver]);

    const skip = (id) => {
        setFlow(previous => ({ ...previous, skipped: { ...previous.skipped, [id]: true } }));
    };

    const colors = avatarColors(icpIndex);
    // Anything beyond what the flow starts with
    const hasProgress = Boolean(flow.image || flow.video)
        || JSON.stringify(without(flow, MEDIA_STEPS)) !== JSON.stringify(without(freshFlow(steps, seed), MEDIA_STEPS));

    const renderBar = (step, index) => {
        const next = steps[index + 1];
        const isDone = Boolean(flow.done[step.id]);
        const isSkipped = !isDone && Boolean(flow.skipped[step.id]);
        return (
            <div className="ls-cflow-bar">
                <div className={`ls-cflow-bar-status${isDone ? ' is-done' : ''}`} aria-live="polite">
                    {isDone ? <Icon name="check_circle" size={18} filled /> : <Icon name={isSkipped ? 'redo' : 'info'} size={18} />}
                    <span>{isDone ? step.done : isSkipped ? 'Skipped. You can come back to it any time.' : step.hint}</span>
                </div>
                <div className="ls-cflow-bar-actions">
                    {step.optional && !isDone && !isSkipped && (
                        <button
                            type="button"
                            className="ls-btn ls-btn-ghost"
                            onClick={() => {
                                skip(step.id);
                                goTo(next.id, { focus: true });
                            }}
                        >
                            Skip
                        </button>
                    )}
                    <button
                        type="button"
                        className="ls-btn ls-btn-primary"
                        disabled={!isDone && !isSkipped}
                        onClick={() => goTo(next.id, { focus: true })}
                    >
                        Next: {next.label}<Icon name="arrow_forward" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    return (
        <CreateContext.Provider value={context}>
            <FlowContext.Provider value={flowValue}>
                <div className="ls-page-header">
                    <div>
                        <div className="ls-eyebrow">Create · {product.name}</div>
                        <h1 className="ls-page-title">{CREATE_KINDS[kind].label}</h1>
                    </div>
                    {icps.length > 0 && (
                        <label className="ls-icp-picker">
                            <div className="ls-avatar" style={{ width: 28, height: 28, fontSize: 11, background: icp ? colors.bg : 'var(--subtle)', color: icp ? colors.fg : 'var(--muted)' }}>
                                {icp ? initials(icp.name) : <Icon name="groups" size={16} />}
                            </div>
                            <select className="ls-select is-bare" value={icpId} onChange={(e) => selectIcp(e.target.value)} aria-label="Write for">
                                <option value="">For anyone</option>
                                {icps.map(item => <option key={item.id} value={item.id}>For {item.name}</option>)}
                            </select>
                        </label>
                    )}
                </div>

                {(seed || initial.resumed) && (
                    <div className="ls-notice ls-cflow-notice">
                        <Icon name={seed ? 'calendar_month' : 'history'} size={18} />
                        <span className="ls-cflow-notice-text">
                            {seed
                                ? <>{typeof seed.from === 'string' && seed.from ? seed.from : 'From your content plan'}: {seed.hook ? `“${seed.hook}”` : seed.topic}</>
                                : 'Picked up where you left off.'}
                        </span>
                        {/* Kept in place while hidden, so the notice doesn't change height when it shows */}
                        <button
                            type="button"
                            className={`ls-link${hasProgress ? '' : ' ls-cflow-invisible'}`}
                            onClick={startOver}
                            tabIndex={hasProgress ? undefined : -1}
                            aria-hidden={hasProgress ? undefined : true}
                        >
                            Start over
                        </button>
                    </div>
                )}

                <div className="ls-tabs ls-cflow-steps" role="tablist" aria-label="Steps" ref={tabsRef}>
                    {steps.map((step, index) => {
                        const isActive = step.id === stepId;
                        const isDone = Boolean(flow.done[step.id]);
                        const isSkipped = !isDone && Boolean(flow.skipped[step.id]);
                        return (
                            <button
                                type="button"
                                role="tab"
                                key={step.id}
                                id={`create-tab-${step.id}`}
                                data-step={step.id}
                                aria-selected={isActive}
                                aria-controls={`create-step-${step.id}`}
                                className={`ls-tab${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}${isSkipped ? ' is-skipped' : ''}`}
                                onClick={() => goTo(step.id)}
                            >
                                <span className="ls-step-num">
                                    {isDone ? <Icon name="check" size={14} /> : index + 1}
                                </span>
                                {step.label}
                                {(isDone || isSkipped) && <span className="ls-cflow-sr">{isDone ? ' (done)' : ' (skipped)'}</span>}
                            </button>
                        );
                    })}
                </div>

                {/* Steps stay mounted so switching between them keeps their work */}
                {steps.map((step, index) => {
                    const next = steps[index + 1];
                    const { Screen } = step;
                    return (
                        <div
                            key={`${step.id}-${version}`}
                            id={`create-step-${step.id}`}
                            className="ls-create-body ls-cflow-panel"
                            hidden={step.id !== stepId}
                            role="tabpanel"
                            aria-labelledby={`create-tab-${step.id}`}
                        >
                            <FlowStepContext.Provider value={{ stepId: step.id, next: next || null, goNext: () => next && goTo(next.id, { focus: true }) }}>
                                <Screen />
                            </FlowStepContext.Provider>
                            {next && renderBar(step, index)}
                        </div>
                    );
                })}
            </FlowContext.Provider>
        </CreateContext.Provider>
    );
};

export default CreateScreen;
